const lerp = (current: number, target: number, factor: number) =>
  current + (target - current) * factor

const lerpAngle = (current: number, target: number, factor: number) => {
  let diff = target - current
  if (diff > 180) diff -= 360
  if (diff < -180) diff += 360
  return current + diff * factor
}

const RAMP_DURATION = 400
const RAMP_SMOOTHING = 0.08

export default (maxTilt = 12, smoothing = 0.6) => ({
  rotateX: 0,
  rotateY: 0,
  maxTilt,
  smoothing,
  shineAngle: 0,
  shineOpacity: 0,
  targetRotateX: 0,
  targetRotateY: 0,
  targetShineAngle: 0,
  targetShineOpacity: 0,
  isHovering: false,
  frame: null as number | null,
  rampStart: null as number | null,

  resetTilt() {
    this.isHovering = false
    this.rampStart = performance.now()
    this.targetRotateX = 0
    this.targetRotateY = 0
    this.targetShineAngle = 0
    this.targetShineOpacity = 0
    this.startLoop()
  },

  tilt(event: MouseEvent) {
    if (!this.isHovering) {
      this.isHovering = true
      this.rampStart = performance.now()
    }
    this.updateTargets(
      event.currentTarget as HTMLElement,
      event.clientX,
      event.clientY,
    )
    this.startLoop()
  },

  startLoop() {
    if (this.frame !== null) {
      return
    }
    this.frame = requestAnimationFrame(() => this.tick())
  },

  currentFactor() {
    if (this.rampStart === null) {
      return this.smoothing
    }

    const progress = (performance.now() - this.rampStart) / RAMP_DURATION

    if (progress >= 1) {
      this.rampStart = null
      return this.smoothing
    }

    return RAMP_SMOOTHING + (this.smoothing - RAMP_SMOOTHING) * progress
  },

  tick() {
    const factor = this.currentFactor()

    this.rotateX = lerp(this.rotateX, this.targetRotateX, factor)
    this.rotateY = lerp(this.rotateY, this.targetRotateY, factor)
    this.shineAngle = lerpAngle(this.shineAngle, this.targetShineAngle, factor)
    this.shineOpacity = lerp(this.shineOpacity, this.targetShineOpacity, factor)

    const isSettled =
      Math.abs(this.rotateX - this.targetRotateX) < 0.02
      && Math.abs(this.rotateY - this.targetRotateY) < 0.02
      && Math.abs(this.shineOpacity - this.targetShineOpacity) < 0.005

    if (!this.isHovering && isSettled) {
      this.snapToTargets()
      this.frame = null
      return
    }

    this.frame = requestAnimationFrame(() => this.tick())
  },

  snapToTargets() {
    this.rotateX = this.targetRotateX
    this.rotateY = this.targetRotateY
    this.shineAngle = this.targetShineAngle
    this.shineOpacity = this.targetShineOpacity
  },

  updateTargets(target: HTMLElement, clientX: number, clientY: number) {
    const rect = target.getBoundingClientRect()
    const x = clientX - rect.left
    const y = clientY - rect.top
    const centerX = rect.width / 2
    const centerY = rect.height / 2
    const dx = (x - centerX) / centerX
    const dy = (y - centerY) / centerY

    this.targetRotateY = dx * this.maxTilt
    this.targetRotateX = -dy * this.maxTilt
    this.targetShineAngle = Math.atan2(dy, dx) * (180 / Math.PI) + 90
    this.targetShineOpacity = Math.min(Math.sqrt(dx * dx + dy * dy) * 0.75, 0.85)
  },

  holoStyle() {
    const opacity = Math.min(this.shineOpacity * 1.35 + 0.25, 1)
    const gradient = `linear-gradient(${this.shineAngle}deg, transparent 8%, rgba(0,0,0,0.65) 30%, rgba(0,0,0,1) 50%, rgba(0,0,0,0.65) 70%, transparent 92%)`
    return `opacity: ${opacity}; -webkit-mask-image: ${gradient}; mask-image: ${gradient}; filter: saturate(1.6) brightness(1.25)`
  },

  glareStyle() {
    return `background: linear-gradient(${this.shineAngle}deg, transparent 0%, rgba(255,255,255,0.1) 42%, rgba(255,255,255,0.5) 50%, rgba(255,255,255,0.1) 58%, transparent 100%); opacity: ${this.shineOpacity * 0.85}`
  },
})
